import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle, ClipboardCheck, Users, TrendingUp, DollarSign, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const ZeroFeesLanding = () => {
  return (
    <div className="min-h-screen bg-white text-gray-900">
      <Helmet>
        <title>Zero Fees - Your Budget is Your Cost | HireX8</title>
        <meta name="description" content="Zero platform fees on HireX8. Every dollar you budget goes to talent and delivery. We make our margin on the contractor side — not yours." />
        <meta property="og:title" content="Zero Fees - Your Budget is Your Cost | HireX8" />
        <meta property="og:description" content="Zero platform fees. Agencies recover 20–40% margin in under 30 days." />
      </Helmet>

      {/* Hero Section - Matching Homepage Style */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-orange-500">
        {/* Background Image */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(/zerofees-hero.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />

        {/* Gradient overlay for text readability - left side */}
        <div className="absolute inset-0 bg-gradient-to-r from-orange-600/95 via-orange-500/80 to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-20">
          <div className="flex flex-col items-start text-left space-y-4 max-w-2xl">
            {/* Logo */}
            <Link to="/" className="mb-4">
              <span className="text-2xl font-bold text-white">HireX8™</span>
            </Link>

            {/* Badge */}
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-white"></div>
              <span className="font-medium text-white/90">Built for Digital Agencies</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none tracking-tight">
              ZERO FEES
            </h1>

            <p className="text-xl sm:text-2xl text-white/90">
              On HireX8, every dollar you budget goes to talent and delivery. We make our margin on the contractor side — not yours.
            </p>

            {/* Feature List */}
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <ClipboardCheck className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg text-white font-medium">No platform fee. No processing markup. No surprises.</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg text-white font-medium">Agencies recover 20–40% margin in under 30 days.</span>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg text-white font-medium">We charge the contractor side — your cost is your cost.</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-start gap-4 pt-4">
              <a
                href="/meet-with-an-expert"
                className="bg-black hover:bg-gray-900 text-white text-lg font-bold px-8 py-4 rounded-lg inline-flex items-center gap-3 transition-all shadow-lg"
              >
                Talk to an Expert
                <ArrowRight className="w-5 h-5" />
              </a>

            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">How It Works</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Zero Fees. <span className="text-orange-500">Zero Surprises.</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Other platforms take 15–20% off the top. On HireX8, that money stays in your agency. Here's exactly how it works.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Set Your Budget</h3>
              <p className="text-gray-600">
                Tell us your budget. That number is your ceiling — we never add fees on top of what you agree to spend.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Get Matched</h3>
              <p className="text-gray-600">
                Our AI matches you with pre-vetted talent based on skill fit, past performance, and project type — not whoever bids lowest.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">100% to Execution</h3>
              <p className="text-gray-600">
                Your full budget reaches the people doing the work. No platform cut. No processing markup. HireX8 earns its margin on the contractor side — not from you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Compare the <span className="text-orange-500">Real Cost</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Let's put real numbers on it. Here's what a $10,000 project actually costs you on each platform.
            </p>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-200">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left p-6 text-gray-600 font-medium">$10,000 Budget</th>
                  <th className="p-6 text-gray-600 font-medium text-center">Upwork / Traditional Platforms</th>
                  <th className="p-6 text-orange-500 font-bold text-center bg-orange-50">HireX8</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="p-6 text-gray-700">Platform Fee</td>
                  <td className="p-6 text-center text-red-500 font-medium">$1,500 - $2,500</td>
                  <td className="p-6 text-center text-orange-500 font-bold bg-orange-50/50">$0</td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="p-6 text-gray-700">Processing Fees</td>
                  <td className="p-6 text-center text-red-500 font-medium">$300 - $500</td>
                  <td className="p-6 text-center text-gray-600 bg-orange-50/50">$5*</td>
                </tr>
                <tr>
                  <td className="p-6 text-gray-900 font-bold">Your Effective Cost</td>
                  <td className="p-6 text-center text-red-500 font-bold">15-25% overhead</td>
                  <td className="p-6 text-center text-orange-500 font-bold text-xl bg-orange-50/50">0.05%**</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-center text-gray-500 text-sm mt-4">
            * $5 processing fee with ACH. 3.5% processing fee with credit card. All other costs go to talent — not to us.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-orange-500" />
              <span className="text-sm text-orange-500 font-medium uppercase tracking-wider">Built for Digital Agencies</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900">
              Protect Your <span className="text-orange-500">Margins</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">White-Label Ready</h3>
              <p className="text-gray-600">
                Deploy talent under your brand. Your clients see your agency — not HireX8. You set the markup. You keep the margin.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Scale Without Risk</h3>
              <p className="text-gray-600">
                No long-term contracts. No minimums. Add capacity when you win work, pull back when you don't. Your overhead stays flat.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pre-Vetted Talent</h3>
              <p className="text-gray-600">
                Every operator is AI-screened and human-verified before they're presented to you. No interviews. No trial-and-error. No wasted time.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Human + AI Power</h3>
              <p className="text-gray-600">
                Our operators work with AI-assisted workflows by default. That means faster output, fewer revisions, and better results — at the same cost.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Objection Handling Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
              <span className="text-sm text-gray-600 font-medium uppercase tracking-wider">Common Questions</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              If it's really zero fees, how does HireX8 make money?
            </h2>
          </div>

          <div className="space-y-10">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed">
                Good question — and the honest answer is: we earn a margin on the contractor side of the transaction, not yours. The talent you're matched with is compensated at a rate we've agreed to with them. You pay what you budget. We don't touch that number. It's the same model that makes a recruiting firm work — except we're not charging you a 25% placement fee on top.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                What about quality? Doesn't zero fees mean lower-tier talent?
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Every operator in HireX8 is AI-screened and human-vetted before they're ever presented. We filter on skill match, past project performance, and communication fit — not on who accepts the lowest rate. If a match doesn't deliver, we replace them at no cost to you. The fee structure funds the platform. The vetting is what protects you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Orange */}
      <section className="py-24 px-6 bg-orange-500">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Stop Paying Platform Fees
          </h2>

          <p className="text-xl text-white/90 mb-10">
            Talk to one of our agency specialists. We'll show you exactly what the same budget gets you on HireX8 versus what you're spending now.
          </p>

          <a
            href="/meet-with-an-expert"
            className="inline-flex items-center gap-3 bg-black hover:bg-gray-900 text-white text-xl font-bold px-12 py-5 rounded-lg shadow-lg transition-all hover:scale-105"
          >
            Book a Free 20-Min Call
            <ArrowRight className="w-6 h-6" />
          </a>

          <p className="mt-6 text-white/70 text-sm">
            No commitment. No sales pitch. We'll run the numbers with you live.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-2xl font-bold text-white">
            HireX8™
          </Link>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <span>© 2026 HireX8. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ZeroFeesLanding;
